import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import {
  useDeleteUser,
  useGenerateUserExposureReport,
  useResendInvite,
  useUser,
  useUserExposureHistory,
} from "@/api/queries/users";
import { useCurrentUser } from "@/api/queries/me";
import { useAuth } from "@/auth/AuthProvider";
import { isRespaiAdmin } from "@/lib/permissions";
import { formatDateTime, formatPpm } from "@/lib/format";

export function PersonnelDetailPage() {
  const { id } = useParams<{ id: string }>();
  const me = useCurrentUser();
  const { identity } = useAuth();
  const canDelete = isRespaiAdmin(me.data?.role ?? identity?.role);
  const user = useUser(id);
  const history = useUserExposureHistory(id);
  const reportJob = useGenerateUserExposureReport(id ?? "");
  const resendInvite = useResendInvite();
  const tz = me.data?.department?.timezone;
  const [from, setFrom] = useState("")
  const [to, setTo] = useState("");

  if (user.isLoading) return <Skeleton className="h-64 w-full" />;
  if (!user.data) return <p className="text-destructive">User not found.</p>;
  const u = user.data;
  return (
    <div className="space-y-6">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">{u.display_name || u.email}</h1>
          <p className="text-sm text-muted-foreground">
            {u.email} · {u.role}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant={u.invite_state === "pending" ? "secondary" : "success"}>
            {u.invite_state === "pending" ? "Pending" : "Active"}
          </Badge>
          {u.invite_state === "pending" ? (
            <Button
              variant="outline"
              size="sm"
              disabled={resendInvite.isPending}
              onClick={() => resendInvite.mutate(u.id)}
            >
              {resendInvite.isPending
                ? "Sending…"
                : resendInvite.isSuccess
                  ? "Invite sent"
                  : "Reinvite"}
            </Button>
          ) : null}
          {canDelete ? (
            <DeleteUserButton userId={u.id} name={u.display_name || u.email} />
          ) : null}
        </div>
      </header>

      <Card>
        <CardHeader>
          <CardTitle>Cumulative exposure</CardTitle>
        </CardHeader>
        <CardContent>
          {history.isLoading ? (
            <Skeleton className="h-32" />
          ) : history.data && history.data.totals.length > 0 ? (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-muted-foreground">
                  <th className="py-1">Gas</th>
                  <th className="py-1">ppm·min</th>
                  <th className="py-1">Peak</th>
                  <th className="py-1">% of NIOSH REL</th>
                </tr>
              </thead>
              <tbody>
                {history.data.totals.map((t, i) => (
                  // flatMap can emit several rows for the same gas (one per
                  // exposure record), so the gas alone isn't a stable key.
                  <tr
                    key={`${t.gas}-${i}`}
                    className="border-t border-border"
                  >
                    <td className="py-1 font-medium">{t.gas}</td>
                    <td className="py-1 tabular-nums">
                      {t.ppm_minutes.toFixed(1)}
                    </td>
                    <td className="py-1 tabular-nums">
                      {formatPpm(t.peak_ppm)}
                    </td>
                    <td
                      className={`py-1 tabular-nums ${
                        t.over_rel ? "font-medium text-destructive" : ""
                      }`}
                    >
                      {t.pct_rel == null ? "—" : `${Math.round(t.pct_rel)}%`}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="text-sm text-muted-foreground">
              No sensor exposure recorded yet.
            </p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Generate personal exposure history report</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap items-end gap-3">
            <label className="text-sm">
              From
              <Input
                type="date"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
                className="mt-1 w-44"
              />
            </label>
            <label className="text-sm">
              To
              <Input
                type="date"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                className="mt-1 w-44"
              />
            </label>
            <Button
              onClick={() => reportJob.mutate({ from, to })}
              disabled={!from || !to || reportJob.isPending}
              data-testid="generate-user-report"
            >
              {reportJob.isPending ? "Queuing…" : "Generate"}
            </Button>
          </div>
          {reportJob.data ? (
            <p className="text-xs text-muted-foreground">
              Queued job <code>{reportJob.data.job_id}</code>. Check Reports
              for status.
            </p>
          ) : null}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Incidents</CardTitle>
        </CardHeader>
        <CardContent>
          {history.data &&
          history.data.incidents &&
          history.data.incidents.length > 0 ? (
            <ul className="divide-y divide-border text-sm">
              {history.data.incidents.map((inc) => (
                <li key={inc.incident_id} className="flex justify-between py-2">
                  <a
                    className="text-primary hover:underline"
                    href={`/incidents/${inc.incident_id}`}
                  >
                    {inc.incident_id.slice(0, 8)}
                  </a>
                  <span>{formatDateTime(inc.dispatched_at, tz)}</span>
                  <span className="tabular-nums">
                    {(inc.ppm_minutes_total ?? 0).toFixed(1)} ppm·min
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-muted-foreground">
              No incidents recorded for this user.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function DeleteUserButton({ userId, name }: { userId: string; name: string }) {
  const navigate = useNavigate();
  const del = useDeleteUser();
  const [confirming, setConfirming] = useState(false);

  if (confirming) {
    return (
      <div className="flex flex-col items-end gap-1">
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">Delete {name}?</span>
          <Button
            variant="destructive"
            size="sm"
            disabled={del.isPending}
            onClick={() =>
              del.mutate(userId, { onSuccess: () => navigate("/personnel") })
            }
          >
            {del.isPending ? "Deleting…" : "Confirm"}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            disabled={del.isPending}
            onClick={() => setConfirming(false)}
          >
            Cancel
          </Button>
        </div>
        {del.isError ? (
          <span className="text-xs text-destructive">
            {del.error instanceof Error
              ? del.error.message
              : "Could not delete user."}
          </span>
        ) : null}
      </div>
    );
  }

  return (
    <Button variant="outline" size="sm" onClick={() => setConfirming(true)}>
      Delete
    </Button>
  );
}
